<script lang="ts">
  // Full-page Texture Pack editor. Each component previews in its REAL context (representative of how
  // it's actually used) — NOT overlaid on the duel board: the Login component renders the login screen
  // + its background; everything else (global / duel-field / card-info / custom) renders the duel
  // screen, where the card-info rail already lives full-height on the LEFT with its chat. The editor
  // docks on the RIGHT and collapses, so it never covers the card-info rail. The seeded duel board
  // survives screen switches — leaveDuel() no-ops in preview.
  import { onDestroy } from "svelte";
  import { fly } from "svelte/transition";
  import { session, go } from "../stores/session.svelte.ts";
  import { editorSel } from "../stores/theme.svelte.ts";
  import { seedSampleDuel, clearSampleDuel } from "../duel/sampleBoard.ts";
  import type { WorldNpc } from "../world/npcs.ts";
  import Duel from "./Duel.svelte";
  import Title from "./Title.svelte";
  import DeckBuilder from "./DeckBuilder.svelte";
  import FreeDuelLobby from "./FreeDuelLobby.svelte";
  import WorldMenu from "../components/WorldMenu.svelte";
  import WorldHud from "../components/WorldHud.svelte";
  import NpcDialog from "../components/NpcDialog.svelte";
  import FieldEditorOverlay from "../components/FieldEditorOverlay.svelte";
  import ThemeEditorControls from "../components/ThemeEditorControls.svelte";
  import { onEscape } from "../hotkeys.ts";

  seedSampleDuel();

  const SCREEN: Record<string, string> = { login: "login", overworld: "overworld", overlay: "overlay", npc: "npc", deck: "deck", freeduel: "freeduel" };
  const screen = $derived(SCREEN[editorSel.id] ?? "duel");
  let collapsed = $state(false);

  // Mock data for the Overworld Menu story (the real menu's actions are no-ops here; it stays open).
  const worldMenuItems = [
    { icon: "trophy", label: "Ranked Duel", desc: "Rated PvP, affects your rank", action: () => {} },
    { icon: "swords", label: "Unranked Duel", desc: "Casual PvP, no rating", action: () => {} },
    { icon: "ranks", label: "Leaderboard", desc: "Top ranked duelists", action: () => {} },
    { icon: "deck", label: "Deck Editor", desc: "Build, craft & dismantle cards", action: () => {} },
    { icon: "edit", label: "Customize Avatar", desc: "Change your duelist's look", action: () => {} },
    { icon: "gear", label: "Settings", desc: "Audio, animations & preferences", action: () => {} },
  ];
  // Mock NPC for the NPC Dialog story (bust omitted — the theming is about the dialog box).
  const mockNpc = { char: "yugi", name: "Yugi Muto", title: "King of Games", canDuel: true, lines: ["So, you've come to challenge me?", "Show me the cards in your heart — let's duel!"] } as unknown as WorldNpc;

  function done(): void {
    clearSampleDuel();
    go(session.mode === "free" ? "freeduel" : "world");
  }
  onEscape(() => (collapsed ? (collapsed = false) : done()));
  onDestroy(() => clearSampleDuel());
</script>

<div class="fixed inset-0 overflow-hidden">
  <!-- the component in its real screen -->
  {#if screen === "login"}
    <Title />
  {:else if screen === "overworld"}
    <WorldMenu items={worldMenuItems} name="Duelist" dp={12345} onClose={() => {}} onLogout={() => {}} />
  {:else if screen === "overlay"}
    <WorldHud name="Duelist" rankLabel="Slifer Red" dp={12345} shards={340} area="Domino City" online={7} onMap={() => {}} onShop={() => {}} onMenu={() => {}} />
  {:else if screen === "npc"}
    <NpcDialog npc={mockNpc} bust={null} onClose={() => {}} onDuel={() => {}} />
  {:else if screen === "deck"}
    <DeckBuilder />
  {:else if screen === "freeduel"}
    <FreeDuelLobby />
  {:else}
    <Duel />
    <!-- keep the field handles even when the editor is collapsed, so you can drag with the full board in view -->
    {#if editorSel.id === "duelfield" && editorSel.fieldHelper}
      <FieldEditorOverlay />
    {/if}
  {/if}

  {#if collapsed}
    <button
      onclick={() => (collapsed = false)}
      class="fixed right-0 top-1/2 z-[70] flex -translate-y-1/2 items-center gap-1.5 rounded-l-md border border-r-0 border-cyan/30 bg-void/90 py-3 pl-2 pr-1.5 backdrop-blur-md transition-colors hover:bg-surface-2/80"
      title="Open Texture Pack editor"
    >
      <svg viewBox="0 0 24 24" class="size-4 text-cyan" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6" /></svg>
      <span class="font-display text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-cyan [writing-mode:vertical-rl]">Texture&nbsp;Pack</span>
    </button>
  {:else}
    <aside class="fixed bottom-0 right-0 top-0 z-[70] flex w-[340px] max-w-[88vw] flex-col border-l border-cyan/25 bg-void/95 backdrop-blur-md" in:fly={{ x: 340, duration: 200 }}>
      <header class="flex shrink-0 items-center justify-between gap-2 border-b border-cyan-dim/30 bg-surface-2/60 px-4 py-2.5">
        <span class="font-display text-[0.72rem] font-semibold uppercase tracking-[0.24em] text-cyan">Texture Pack</span>
        <span class="flex items-center gap-1.5">
          <button onclick={() => (collapsed = true)} class="grid size-7 place-items-center rounded text-ink-dim transition-colors hover:bg-void/60 hover:text-cyan" title="Collapse" aria-label="Collapse editor">
            <svg viewBox="0 0 24 24" class="size-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6" /></svg>
          </button>
          <button onclick={done} class="chamfer-btn bg-void/60 px-2.5 py-1 font-display text-[0.6rem] font-semibold uppercase tracking-[0.16em] text-ink-dim transition-colors hover:text-cyan" style="--c:5px">Done</button>
        </span>
      </header>
      <div class="min-h-0 flex-1 overflow-y-auto scroll-cyber">
        <ThemeEditorControls />
      </div>
    </aside>
  {/if}
</div>
